import {
  of,
  BehaviorSubject,
  catchError,
  tap,
  filter,
  map,
  Subject,
  debounceTime,
  takeUntil,
  distinctUntilChanged,
  delay,
} from "rxjs";
import ls from "../../../common/helpers/localStorageActions";
import dataLoader from "../services/dataLoader";

function errorHandler(e) {
  console.error(e);
  return of(null);
}

const emptyCallbacks = {
  onDataReceived: () => {},
  onTrackDimensionsChange: () => {},
  onRangeChange: () => {},
  onFiltersChange: () => {},
  onFilterHighlight: () => {},
  dispose: () => {},
  handleVisibleRangeStabilized: () => {},
  onInit: () => {},
  onRangeStabilized: () => {},
  onSharedStateChange: () => {},
  onRegionStateChange: () => {},
  onPreferencesChanged: () => {},
  onStrandInverted: () => {},
  onHighlightedRegionChanged: () => {},
  getState: () => null,
  setState: () => null,
};

class Track {
  constructor(options, stage, zoomService) {
    const { controller, ...otherOptions } = options;
    const collapsedState = ls.getPulsarCollapsedState() || {};
    const conf = otherOptions;

    this.conf = conf;
    this.title = conf.title;
    this.id = conf.id;
    this.helperTextId = conf.helperTextId || conf.title;

    this.isCollapsed = Boolean(collapsedState[this.id]);
    this.lastReceivedRange = null;
    this.stage = stage;
    this.variantRegistry = stage.variantRegistry;
    this.zoomService = zoomService;
    this.isDisposed = false;
    this.apiLoader = null;

    // observers
    this.destroyed$ = new Subject();
    this.updated$ = new BehaviorSubject(null);
    this.filtersUpdated$ = new BehaviorSubject(null);
    this.axisUpdated$ = new BehaviorSubject({});
    this.overlayUpdated$ = new BehaviorSubject(null);
    this.processing$ = new BehaviorSubject(0);
    this.sharedStateUpdate$ = new Subject();
    this.heightUpdated$ = new BehaviorSubject();
    this.isValidRange$ = new BehaviorSubject(true);

    // renderers
    this.FilterRenderer = conf.FilterRenderer;
    this.TooltipRenderer = conf.TooltipRenderer;
    this.TitleRenderer = conf.TitleRenderer;
    this.AxisRenderer = conf.AxisRenderer;
    this.GridRenderer = conf.GridRenderer;
    this.OverlayRenderer = conf.OverlayRenderer;
    this.TickRenderer = conf.TickRenderer;

    // positioning
    this.y0 = 0;
    this.y1 = 0;
    this.contentHeight = 0;
    this.headerHeight = conf.headerHeight ?? 18;
    this.pTop = conf.pTop || 0;
    this.pBottom = conf.pBottom || 0;
    this.isStickyTop = Boolean(conf.isStickyTop);
    this.isOverlay = Boolean(conf.isOverlay);
    this.totalHeight = 0;
    this.offset = 0;
    this.absoluteOffsetY = 0;

    this.controller = { ...emptyCallbacks, ...controller(this) };
    this.meshes = this.controller.meshes || [];
    this.controller.onInit();

    this.createNewLoader();
    this.setHeight(conf.contentHeight || 100);
    this.recalcHeight();
    this.listenToResizeForRaycast();
    this.registerListeners();
  }

  registerListeners = () => {
    this.stage.state$
      .pipe(debounceTime(50), takeUntil(this.destroyed$))
      .subscribe(() => this.controller.onSharedStateChange());

    this.stage.preferences$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.controller.onPreferencesChanged());
  };

  listenToResizeForRaycast = () => {
    const onResize = () => {
      this.meshes
        .filter((x) => x.canRaycast())
        .reduce((a, m) => m.setRaycastOffset(a), 1);
    };

    this.meshes
      .filter((x) => x.canRaycast())
      .forEach((mesh) => mesh.addResizeListener(onResize));
  };

  createNewLoader = () => {
    this.apiLoader = dataLoader(this.conf.apiLoader || {});

    this.apiLoader.regionsUpdate$
      .pipe(
        filter(() => !this.isStickyTop),
        map((x) => ({ [this.id]: x })),
        catchError(() => of({})),
        takeUntil(this.destroyed$)
      )
      .subscribe((x) => this.stage.setRegionState(x));

    this.apiLoader.response$
      .pipe(
        tap(() => this.processing$.next(1)),
        tap(() => this.zoomService.onBlockStarted()),
        delay(20),
        tap((data) => data && this.controller.onDataReceived(data)),
        tap(() => this.zoomService.onBlockEnded()),
        tap(() => this.processing$.next(0)),
        catchError(errorHandler),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.apiLoader.isValidRange$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((x) => this.isValidRange$.next(x));

    this.zoomService.isInverted$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => this.controller.onStrandInverted());
  };

  setSharedState = (newState) => {
    const current = this.stage.state$.value || {};
    this.stage.setState({ ...current, ...newState });
  };

  getSharedState = () => {
    return this.stage.state$.value || {};
  };

  onRegionStateChange = () => {
    this.controller.onRegionStateChange();
  };

  getRegionState = () => {
    return {
      highlightedRegion: this.stage.highlightedRegion,
      regionState: this.stage.regionState$.value,
    };
  };

  onHighlightedRegionChanged = () => {
    this.controller.onHighlightedRegionChanged();
  };

  getHighlightedRegion = () => {
    return this.stage.highlightedRegion;
  };

  updateProjectionMatrix = (offset, totalHeight, laneTop) => {
    this.laneTop = laneTop;
    this.absoluteOffsetY = offset + laneTop + this.headerHeight;
    this.offset = offset + this.headerHeight + this.pTop;
    const heightPercent = this.contentHeight / totalHeight;
    const totalTrackHeight = this.contentHeight + this.pTop + this.pBottom;
    const prevHeightPercent = this.offset / totalHeight;
    const scaleY = this.isCollapsed ? 0 : heightPercent;
    const transformY = this.isCollapsed ? -1 : prevHeightPercent;
    const pTopPercent = this.pTop / totalHeight;
    const planeStart = transformY - pTopPercent;
    const planeSize = totalTrackHeight / totalHeight;

    this.y0 = planeStart;
    this.y1 = planeStart + planeSize;

    const height = this.totalHeight - (this.pTop + this.pBottom);
    const clipY0 = 0 - this.pTop / height;
    const clipY1 = 1 + this.pBottom / height;

    this.meshes.forEach((m, i) => {
      m.setBoundingSphere(scaleY, transformY, i + 3);
      m.material.uniforms.u_y0.value = clipY0;
      m.material.uniforms.u_y1.value = clipY1;
    });
  };

  toggle = () => {
    this.isCollapsed = !this.isCollapsed;
    this.recalcHeight();

    if (this.isCollapsed) {
      this.reset();
    } else {
      this.emitPosition();
    }

    const stored = ls.getPulsarCollapsedState() || {};
    ls.setPulsarCollapsedState({ ...stored, [this.id]: this.isCollapsed });
  };

  recalcHeight = () => {
    const contentHeight = this.contentHeight + this.pTop + this.pBottom;
    const bodyHeight = this.isCollapsed ? 0 : contentHeight;
    this.totalHeight = this.headerHeight + bodyHeight;
    this.bodyHeight = bodyHeight;
    this.heightUpdated$.next({});
  };

  requestRender = () => {
    this.updated$.next();
  };

  calcHeight = () => {
    return this.isCollapsed ? this.headerHeight : this.totalHeight;
  };

  setHeight = (newHeight) => {
    if (this.contentHeight === newHeight) return;
    this.contentHeight = newHeight;
    this.meshes.forEach((x) => x.setHeight(newHeight));
    this.recalcHeight();
  };

  updateTrackDimensions = (trackDimensions) => {
    this.controller.onTrackDimensionsChange(trackDimensions);
  };

  setAxisProps = (data) => {
    this.axisUpdated$.next({ ...data });
  };

  getStrandInverted = () => {
    return this.zoomService.isInverted$.value;
  };

  setOverlayProps = (overlayData) => {
    this.overlayUpdated$.next(overlayData);
  };

  setFilters = (filters) => {
    this.filtersUpdated$.next(filters);
  };

  loadPosition = (range, reqParams) => {
    this.apiLoader.setPosition(range, reqParams);
  };

  applyFilters = (filters) => {
    this.controller.onFiltersChange(filters);
  };

  applyHighlightFilters = (filters) => {
    this.controller.onFilterHighlight(filters);
  };

  reset = () => {
    this.controller.meshes.forEach((x) => x.reset());
    this.apiLoader.destroy();
    this.controller.onReset();
    this.controller.onInit();
    this.controller.onPreferencesChanged(this.stage.preferences$.value);
    this.createNewLoader();
  };

  getPreferences = () => {
    return this.stage.preferences$.value;
  };

  emitPosition = () => {
    if (this.lastReceivedRange) {
      this.controller.onRangeChange(this.lastReceivedRange);
      this.controller.onRangeStabilized(this.lastReceivedRange);
    }
  };

  dispose = () => {
    if (this.isDisposed) return;
    this.isDisposed = true;
    this.isValidRange$.complete();
    this.processing$.complete();
    this.updated$.complete();
    this.filtersUpdated$.complete();
    this.axisUpdated$.complete();
    this.overlayUpdated$.complete();
    this.heightUpdated$.complete();
    this.destroyed$.next();
    this.meshes.forEach((x) => x.dispose());
    this.controller.onReset();
    this.variantRegistry = null;
    this.apiLoader.destroy();
    this.apiLoader = null;
  };

  visibleRangeUpdated = (range) => {
    this.lastReceivedRange = range;
    if (this.isCollapsed) return;
    this.controller.onRangeChange(range);
  };

  getState = () => {
    return this.controller.getState();
  };

  setState = (updated) => {
    return this.controller.setState(updated);
  };

  visRangeStabilized = (range) => {
    this.lastReceivedRange = range;
    if (this.isCollapsed) return;
    this.controller.onRangeStabilized(range);
  };

  getTrackOptions = () => {
    const trackType = this.conf.trackType || this.conf.id;
    const options = ls.getPulsarTrackOptions() || {};
    return options[trackType] || {};
  };

  setTrackOptions = (trackOptions) => {
    const trackType = this.conf.trackType || this.conf.id;
    const options = ls.getPulsarTrackOptions() || {};
    options[trackType] = trackOptions;
    ls.setPulsarTrackOptions(options);
  };
}

export default Track;
